import { useState, useEffect, useCallback } from 'react';
import seoAiCacheService from '@/services/seoAiCache';
import { SerpAnalysisResult, SerpSummary, ContentOptimizationResult, InternalLinkFinderResult } from '@/services/seoAi';

export function useKeywordCache(keywordId: string | undefined) {
  const [serpAnalysis, setSerpAnalysis] = useState<SerpAnalysisResult | null>(null);
  const [serpSummary, setSerpSummary] = useState<SerpSummary | null>(null);
  const [contentOptimization, setContentOptimization] = useState<ContentOptimizationResult | null>(null);
  const [internalLinks, setInternalLinks] = useState<InternalLinkFinderResult | null>(null);
  const [cacheLoaded, setCacheLoaded] = useState(false);

  useEffect(() => {
    if (!keywordId || cacheLoaded) return;

    const loadCache = async () => {
      try {
        const [cachedSerp, cachedOpt, cachedLinks] = await Promise.all([
          seoAiCacheService.getSerpAnalysis(keywordId),
          seoAiCacheService.getContentOptimization(keywordId),
          seoAiCacheService.getInternalLinkSuggestions(keywordId),
        ]);

        if (cachedSerp) {
          const serpResult: SerpAnalysisResult = {
            competitors: cachedSerp.serpResults,
            summary: cachedSerp.summary,
          };
          setSerpAnalysis(serpResult);
          setSerpSummary(cachedSerp.summary);
        }

        if (cachedOpt) {
          setContentOptimization({
            overallScore: cachedOpt.overallScore ?? 0,
            scores: cachedOpt.scores,
            recommendations: cachedOpt.recommendations,
          });
        }

        if (cachedLinks) {
          setInternalLinks(cachedLinks.suggestions);
        }
      } catch {
        // Cache loading is best-effort
      }
      setCacheLoaded(true);
    };

    loadCache();
  }, [keywordId, cacheLoaded]);

  const handleSerpAnalysisComplete = useCallback((result: SerpAnalysisResult | null) => {
    setSerpAnalysis(result);
    setSerpSummary(result?.summary ?? null);
  }, []);

  return {
    serpAnalysis,
    serpSummary,
    contentOptimization,
    internalLinks,
    setSerpAnalysis,
    setSerpSummary,
    handleSerpAnalysisComplete,
  };
}
